import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
    Card,
    Layout,
    Text,
    Button,
    BlockStack,
    InlineStack,
    Spinner,
    Banner,
    Badge,
    DataTable,
    Pagination,
    ProgressBar,
    Checkbox,
    TextField,
} from '@shopify/polaris';
import { Scanner } from '@yudiel/react-qr-scanner';
import '@shopify/polaris/build/esm/styles.css';

import { printingsService } from '../services/printingsService';

const PrintVerificationPage = () => {
    const { printingId } = useParams();
    const [searchParams] = useSearchParams();
    const lane = searchParams.get('lane') || 'hard_cover';
    const printingProfile = {
        hard_cover: 'premium',
        booklet: 'booklet',
        pristol_express: 'standard',
        booklet_express: 'booklet'
    }[lane] || 'premium';

    // State
    const [generations, setGenerations] = useState([]);
    const [stats, setStats] = useState({ total: 0, printed: 0, pending: 0, percentComplete: 0 });
    const [printingDate, setPrintingDate] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({ pages: 1, total: 0 });

    // Scanner mode
    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const [lastScanned, setLastScanned] = useState(null);
    const [scanError, setScanError] = useState(null);
    const [isProcessingScan, setIsProcessingScan] = useState(false);

    // Selection for bulk actions
    const [selectedIds, setSelectedIds] = useState([]);
    const [isBulkProcessing, setIsBulkProcessing] = useState(false);

    // Manual search
    const [searchQuery, setSearchQuery] = useState('');

    // Audio ref for beep sound
    const successAudioRef = useRef(null);
    const errorAudioRef = useRef(null);
    const loadRequestId = useRef(0);

    // Load generations
    const loadGenerations = useCallback(async (page = 1) => {
        const requestId = ++loadRequestId.current;
        const idToFetch = printingId || 'pending';

        try {
            setIsLoading(true);
            setError(null);

            const response = await printingsService.getGenerationsForPrinting(
                idToFetch,
                page,
                50,
                null,
                printingProfile,
                lane
            );

            if (requestId !== loadRequestId.current) return;
            if (response.success) {
                setGenerations(response.data || []);
                setStats(response.stats || { total: 0, printed: 0, pending: 0, percentComplete: 0 });
                setPagination(response.pagination || { pages: 1, total: 0 });
                setPrintingDate(response.printingDate);
            } else {
                setError(response.error || 'Failed to load generations');
            }
        } catch (err) {
            if (requestId === loadRequestId.current) {
                setError(err.message || 'An error occurred');
            }
        } finally {
            if (requestId === loadRequestId.current) setIsLoading(false);
        }
    }, [lane, printingId, printingProfile]);

    useEffect(() => {
        loadGenerations(currentPage);
    }, [loadGenerations, currentPage]);

    // Handle header refresh
    useEffect(() => {
        const handleRefresh = () => {
            loadGenerations(currentPage);
        };
        const handleOpenScanner = () => {
            if (printingId) setIsScannerOpen(true);
        };
        window.addEventListener('printVerificationRefresh', handleRefresh);
        window.addEventListener('printingsVerifyOpenScanner', handleOpenScanner);
        return () => {
            window.removeEventListener('printVerificationRefresh', handleRefresh);
            window.removeEventListener('printingsVerifyOpenScanner', handleOpenScanner);
        };
    }, [loadGenerations, currentPage, printingId]);

    // Handle barcode scan
    const handleScan = useCallback(async (result) => {
        if (isProcessingScan || !result || result.length === 0) return;

        const scannedValue = result[0].rawValue;
        if (!scannedValue) return;

        // Prevent duplicate scans within 2 seconds
        if (lastScanned?.barcode_id === scannedValue &&
            Date.now() - lastScanned.timestamp < 2000) {
            return;
        }

        setIsProcessingScan(true);
        setScanError(null);

        try {
            // Pass printingId if available, otherwise it's undefined (global mode)
            const response = await printingsService.markPrintedByBarcode(
                scannedValue,
                printingId,
                printingProfile,
                lane
            );

            if (response.success) {
                setLastScanned({
                    barcode_id: scannedValue,
                    kid_name: response.kid_name,
                    order_id: response.order_id,
                    story_slug: response.story_slug,
                    already_printed: response.already_printed,
                    timestamp: Date.now()
                });

                // Play success sound
                if (successAudioRef.current) {
                    successAudioRef.current.play().catch(() => { });
                }

                if (!response.already_printed) {
                    await loadGenerations(currentPage);
                }
            } else {
                setScanError(response.error || 'Failed to mark as printed');
                if (errorAudioRef.current) {
                    errorAudioRef.current.play().catch(() => { });
                }
            }
        } catch (err) {
            setScanError(err.message || 'Scan error');
            if (errorAudioRef.current) {
                errorAudioRef.current.play().catch(() => { });
            }
        } finally {
            setIsProcessingScan(false);
        }
    }, [
        currentPage,
        isProcessingScan,
        lane,
        lastScanned,
        loadGenerations,
        printingId,
        printingProfile
    ]);

    // Handle manual search/mark
    const handleManualMark = async () => {
        if (!searchQuery.trim()) return;

        setIsProcessingScan(true);
        setScanError(null);

        try {
            const response = await printingsService.markPrintedByBarcode(
                searchQuery.trim(),
                printingId,
                printingProfile,
                lane
            );

            if (response.success) {
                setLastScanned({
                    barcode_id: searchQuery.trim(),
                    kid_name: response.kid_name,
                    order_id: response.order_id,
                    story_slug: response.story_slug,
                    already_printed: response.already_printed,
                    timestamp: Date.now()
                });
                setSearchQuery('');

                if (!response.already_printed) {
                    await loadGenerations(currentPage);
                }
            } else {
                setScanError(response.error || 'Generation not found');
            }
        } catch (err) {
            setScanError(err.message || 'Error marking as printed');
        } finally {
            setIsProcessingScan(false);
        }
    };

    // Handle bulk mark as printed
    const handleBulkMarkPrinted = async () => {
        if (!printingId || selectedIds.length === 0) return;

        setIsBulkProcessing(true);
        setError(null);

        try {
            const response = await printingsService.bulkMarkPrinted(
                selectedIds,
                printingId,
                lane
            );

            if (response.success) {
                // Reload the page to get updated data
                await loadGenerations(currentPage);
                setSelectedIds([]);
            } else {
                setError(response.error || 'Failed to bulk update');
            }
        } catch (err) {
            setError(err.message || 'Bulk update error');
        } finally {
            setIsBulkProcessing(false);
        }
    };

    // Toggle selection
    const toggleSelection = (id) => {
        setSelectedIds(prev =>
            prev.includes(id)
                ? prev.filter(x => x !== id)
                : [...prev, id]
        );
    };

    // Select/deselect all pending
    const toggleSelectAllPending = () => {
        const pendingIds = generations
            .filter(g => g.status !== 'printed')
            .map(g => g._id);

        const allSelected = pendingIds.every(id => selectedIds.includes(id));

        if (allSelected) {
            setSelectedIds(prev => prev.filter(id => !pendingIds.includes(id)));
        } else {
            setSelectedIds(prev => [...new Set([...prev, ...pendingIds])]);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status, printedAt) => {
        if (status === 'printed') {
            return (
                <Badge tone="success">
                    ✅ Printed {printedAt ? formatTime(printedAt) : ''}
                </Badge>
            );
        }
        return <Badge tone="attention">⏳ Pending</Badge>;
    };

    // Scanner View
    if (isScannerOpen) {
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: '#000',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* Header */}
                <div style={{
                    padding: '16px',
                    background: 'rgba(0,0,0,0.8)',
                    color: 'white'
                }}>
                    <BlockStack gap="200">
                        <Text variant="headingMd" as="h2" tone="inherit">
                            Scan to Verify Print
                        </Text>

                        <Text variant="bodySm" tone="subdued">
                            {printingId ? `Batch: ${formatDate(printingDate)}` : 'Global Verification'}
                        </Text>

                        {/* Only show progress bar if not global (since global starts at 0 printed in this view) */}
                        {printingId && (
                            <>
                                <ProgressBar progress={stats.percentComplete} size="small" tone="primary" />
                                <Text variant="bodySm" tone="inherit">
                                    {stats.printed} / {stats.total} verified ({stats.percentComplete}%)
                                </Text>
                            </>
                        )}

                        {!printingId && (
                            <Text variant="bodySm" tone="inherit">
                                {stats.pending} items pending
                            </Text>
                        )}
                    </BlockStack>
                </div>

                {/* Scanner */}
                <div style={{ flex: 1, position: 'relative' }}>
                    <Scanner
                        onScan={handleScan}
                        formats={['code_128', 'code_39', 'ean_13', 'qr_code']}
                        scanDelay={1000}
                        allowMultiple={true}
                        styles={{
                            container: { width: '100%', height: '100%' },
                            video: { objectFit: 'cover' }
                        }}
                    />

                    {isProcessingScan && (
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            background: 'rgba(0,0,0,0.7)',
                            padding: '20px',
                            borderRadius: '8px'
                        }}>
                            <Spinner size="large" />
                        </div>
                    )}
                </div>

                {/* Last Scanned Info */}
                <div style={{
                    padding: '16px',
                    background: lastScanned?.already_printed ? '#fef3cd' : '#d4edda',
                    borderTop: '1px solid rgba(255,255,255,0.2)'
                }}>
                    {lastScanned ? (
                        <BlockStack gap="100">
                            <Text variant="bodyMd" fontWeight="bold">
                                {lastScanned.already_printed ? '⚠️ Already Printed' : '✅ Marked as Printed'}
                            </Text>
                            <Text variant="bodySm">
                                {lastScanned.barcode_id}
                            </Text>
                            <Text variant="bodySm">
                                {lastScanned.kid_name} - {lastScanned.story_slug}
                            </Text>
                            <Text variant="bodySm" tone="subdued">
                                Order: {lastScanned.order_id}
                            </Text>
                        </BlockStack>
                    ) : (
                        <Text variant="bodyMd" tone="subdued">
                            Point camera at barcode to scan
                        </Text>
                    )}

                    {scanError && (
                        <Banner tone="critical" style={{ marginTop: '8px' }}>
                            {scanError}
                        </Banner>
                    )}
                </div>

                {/* Footer Actions */}
                <div style={{
                    padding: '16px',
                    background: '#1a1a1a',
                    display: 'flex',
                    gap: '12px'
                }}>
                    <Button onClick={() => setIsScannerOpen(false)} size="large">
                        Stop Scanning
                    </Button>
                    <Button onClick={() => { setIsScannerOpen(false); }} variant="plain" tone="critical">
                        View List
                    </Button>
                </div>

                {/* Audio elements for feedback */}
                <audio ref={successAudioRef} src="data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU..." preload="auto" />
                <audio ref={errorAudioRef} src="data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU..." preload="auto" />
            </div>
        );
    }

    // List View
    return (
        <Layout>
            {/* Progress Card */}
            <Layout.Section>
                <Card>
                    <BlockStack gap="400">
                        <Text variant="headingMd" as="h2">
                            {printingId ? "Verification Progress" : "Pending Queue Status"}
                        </Text>

                        {printingId && (
                            <div style={{ marginBottom: '8px' }}>
                                <ProgressBar
                                    progress={stats.percentComplete}
                                    size="medium"
                                    tone={stats.percentComplete === 100 ? 'success' : 'primary'}
                                />
                            </div>
                        )}

                        <InlineStack gap="400" wrap={false}>
                            <div style={{
                                flex: 1,
                                padding: '16px',
                                background: '#f6f6f7',
                                borderRadius: '8px',
                                textAlign: 'center'
                            }}>
                                <Text variant="headingXl" as="p">{stats.total}</Text>
                                <Text variant="bodySm" tone="subdued">Total</Text>
                            </div>
                            <div style={{
                                flex: 1,
                                padding: '16px',
                                background: '#d4edda',
                                borderRadius: '8px',
                                textAlign: 'center'
                            }}>
                                <Text variant="headingXl" as="p" tone="success">{stats.printed}</Text>
                                <Text variant="bodySm" tone="subdued">Printed</Text>
                            </div>
                            <div style={{
                                flex: 1,
                                padding: '16px',
                                background: '#fff3cd',
                                borderRadius: '8px',
                                textAlign: 'center'
                            }}>
                                <Text variant="headingXl" as="p" tone="caution">{stats.pending}</Text>
                                <Text variant="bodySm" tone="subdued">Pending</Text>
                            </div>
                        </InlineStack>
                    </BlockStack>
                </Card>
            </Layout.Section>

            {/* Manual Entry */}
            {printingId ? (
                <Layout.Section>
                    <Card>
                        <BlockStack gap="300">
                            <Text variant="headingMd" as="h2">
                                Manual Entry
                            </Text>
                        <InlineStack gap="200" blockAlign="end">
                            <div style={{ flex: 1 }}>
                                <TextField
                                    label="Barcode ID"
                                    value={searchQuery}
                                    onChange={setSearchQuery}
                                    placeholder="Enter barcode number..."
                                    autoComplete="off"
                                    connectedRight={
                                        <Button
                                            onClick={handleManualMark}
                                            loading={isProcessingScan}
                                            disabled={!searchQuery.trim()}
                                        >
                                            Mark Printed
                                        </Button>
                                    }
                                />
                            </div>
                        </InlineStack>

                        {scanError && (
                            <Banner tone="critical" onDismiss={() => setScanError(null)}>
                                {scanError}
                            </Banner>
                        )}

                        {lastScanned && !isScannerOpen && (
                            <Banner
                                tone={lastScanned.already_printed ? 'warning' : 'success'}
                                onDismiss={() => setLastScanned(null)}
                            >
                                {lastScanned.already_printed ? 'Already printed: ' : 'Marked as printed: '}
                                {lastScanned.kid_name} - {lastScanned.story_slug} ({lastScanned.order_id})
                            </Banner>
                        )}
                        </BlockStack>
                    </Card>
                </Layout.Section>
            ) : (
                <Layout.Section>
                    <Banner
                        tone="info"
                        title="Select a batch to verify"
                    >
                        Verification mutations require an immutable printing batch. Choose a batch below before scanning or manually marking an item.
                    </Banner>
                </Layout.Section>
            )}

            {/* Generations List */}
            <Layout.Section>
                <Card>
                    <BlockStack gap="400">
                        <InlineStack align="space-between">
                            <Text variant="headingMd" as="h2">
                                Generations ({pagination.total})
                            </Text>

                            {printingId && <InlineStack gap="200">
                                {selectedIds.length > 0 && (
                                    <Button
                                        variant="primary"
                                        onClick={handleBulkMarkPrinted}
                                        loading={isBulkProcessing}
                                    >
                                        Mark {selectedIds.length} as Printed
                                    </Button>
                                )}
                                <Button
                                    onClick={toggleSelectAllPending}
                                    disabled={generations.filter(g => g.status !== 'printed').length === 0}
                                >
                                    {generations.filter(g => g.status !== 'printed').every(g => selectedIds.includes(g._id))
                                        ? 'Deselect All Pending'
                                        : 'Select All Pending'
                                    }
                                </Button>
                            </InlineStack>}
                        </InlineStack>

                        {error && (
                            <Banner tone="critical" onDismiss={() => setError(null)}>
                                {error}
                            </Banner>
                        )}

                        {isLoading ? (
                            <div style={{ padding: '40px', textAlign: 'center' }}>
                                <Spinner size="large" />
                            </div>
                        ) : generations.length === 0 ? (
                            <div style={{ padding: '20px', textAlign: 'center' }}>
                                <Text variant="bodyMd" tone="subdued">
                                    No generations found
                                </Text>
                            </div>
                        ) : (
                            <>
                                <DataTable
                                    columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text']}
                                    headings={['', 'Order ID', 'Kid Name', 'Story', 'Barcode', 'Profile', 'Epoch', 'Status', 'Batch']}
                                    rows={generations.map((gen) => [
                                        <Checkbox
                                            label=""
                                            labelHidden
                                            checked={selectedIds.includes(gen._id)}
                                            onChange={() => toggleSelection(gen._id)}
                                            disabled={!printingId || gen.status === 'printed'}
                                        />,
                                        gen.order_id || '-',
                                        <span>
                                            {gen.kid_name || '-'}
                                            {gen.is_express && <Badge tone="info" size="small">Express</Badge>}
                                            {gen.is_premium_printing && <Badge tone="attention" size="small">Premium</Badge>}
                                            {gen.is_gift && <Badge tone="attention" size="small">Gift</Badge>}
                                        </span>,
                                        gen.story_slug || '-',
                                        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                            {gen.barcode_id || '-'}
                                        </span>,
                                        gen.profile_selection?.profile_key
                                            ? `${gen.profile_selection.profile_key} · revision ${gen.profile_selection.revision}`
                                            : '-',
                                        gen.printing?.artifact_epoch ?? gen.print_artifacts?.epoch ?? '-',
                                        getStatusBadge(gen.status, gen.printed_at),
                                        printingId
                                            ? printingId
                                            : gen.printing_id
                                                ? (
                                                    <a
                                                        href={`/printings/${encodeURIComponent(gen.printing_id)}/verify?lane=${encodeURIComponent(lane)}`}
                                                        aria-label={`Verify batch ${gen.printing_id}`}
                                                    >
                                                        Verify batch
                                                    </a>
                                                )
                                                : 'Batch unavailable'
                                    ])}
                                />

                                {/* Pagination */}
                                {pagination.pages > 1 && (
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginTop: '16px',
                                        padding: '0 4px'
                                    }}>
                                        <Text variant="bodySm" tone="subdued">
                                            Page {currentPage} of {pagination.pages}
                                        </Text>
                                        <Pagination
                                            hasPrevious={currentPage > 1}
                                            onPrevious={() => setCurrentPage(currentPage - 1)}
                                            hasNext={currentPage < pagination.pages}
                                            onNext={() => setCurrentPage(currentPage + 1)}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </BlockStack>
                </Card>
            </Layout.Section>
        </Layout>
    );
};

export default PrintVerificationPage;
